import React from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import './EditorCard.css';

interface EditorCardProps {
  expanded: boolean;
  onToggle: () => void;
  /** Header content (label, monospace id, chips) — sits between the chevron and actions. */
  title: React.ReactNode;
  /** Right-aligned action buttons (move/delete) — siblings of the toggle, so clicks never toggle the card. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Collapsible section/item card for the Workflow catalog editors (Task Lists,
 * Template Task Config, Ticket Type Template). Replaces MUI `Accordion` with the
 * card-on-grey design system: a floating white card, a disclosure-chevron header
 * with recessive Tabler icon actions, and a hairline-divided body. See the
 * `sm4rt-web-ui` skill §4/§9.
 *
 * The toggle and the action buttons are siblings (not nested) so each is an
 * independent control — the toggle's accessible name is just the title, and a
 * `getByRole('button', { name: /delete/i })` resolves to the delete action only.
 */
export const EditorCard: React.FC<EditorCardProps> = ({
  expanded,
  onToggle,
  title,
  actions,
  children,
}) => (
  <div className={`editor-card ${expanded ? 'editor-card--open' : ''}`}>
    <div className="editor-card__header">
      <div
        className="editor-card__toggle"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <IconChevronDown
          size={15}
          stroke={1.5}
          className={`editor-card__chev ${expanded ? 'editor-card__chev--open' : ''}`}
        />
        <div className="editor-card__title">{title}</div>
      </div>
      {actions && <div className="editor-card__actions">{actions}</div>}
    </div>
    {expanded && <div className="editor-card__body">{children}</div>}
  </div>
);
